import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode } from '@/types/node-types';
import {
  describeLoopsCached,
  loopContaining,
  nodeLabel,
  regionRole,
  type RegionRole,
  type RepeatLoop,
} from '@/pages/workflow/helper/repeat-loop';
import {
  regionNodeRun,
  type RegionNodeRun,
} from '@/pages/workflow/helper/repeat-run';
import { cn } from '@/lib/utils';

export interface RegionMembership {
  loop: RepeatLoop;
  role: RegionRole;
  carrierName: string;
  /** What this node did inside the loop on the last run, if the carrier ran. */
  run: RegionNodeRun | null;
}

/** Which loop (if any) a node is inside, and what it did there on the last run. */
export function useRegionMembership(nodeId: string): RegionMembership | null {
  const { nodes, edges } = useNodeStore(
    useShallow((state) => ({ nodes: state.nodes, edges: state.edges })),
  );
  return useMemo(() => {
    const loops = describeLoopsCached(nodes, edges);
    const loop = loopContaining(loops, nodeId);
    if (!loop) return null;
    const role = regionRole(loop, nodeId);
    if (!role) return null;
    const carrier = nodes.find(
      (node: CustomNode) => node.id === loop.carrierId,
    );
    return {
      loop,
      role,
      carrierName: nodeLabel(carrier),
      run: regionNodeRun(carrier?.data?.result, nodeId),
    };
  }, [nodes, edges, nodeId]);
}

const ROLE_WORDS: Record<RegionRole, string> = {
  entry: 'repeated step',
  evaluator: 'judge',
  step: 'inside the loop',
};

/**
 * The small chip a node inside a loop wears on the canvas: "repeats" before
 * a run, "ran 3 times" after one. Region nodes never read as skipped.
 */
export function RepeatRegionChip({ nodeId }: { nodeId: string }) {
  const membership = useRegionMembership(nodeId);
  if (!membership) return null;
  const { role, run, carrierName } = membership;
  return (
    <div
      className='mx-3 mb-1 flex items-center gap-1.5 rounded-md border border-amber-200 dark:border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/15 px-2 py-1 text-[11px] text-amber-900 dark:text-amber-100'
      data-testid='repeat-region-chip'
      data-role={role}
      title={`${ROLE_WORDS[role]} of the loop "${carrierName}"`}
    >
      <RotateCcw className='size-3' />
      <span className='font-medium'>
        {run
          ? `ran ${run.count} ${run.count === 1 ? 'time' : 'times'}${run.skipped ? `, skipped ${run.skipped}` : ''}`
          : 'repeats'}
      </span>
      <span className='truncate text-amber-800/80 dark:text-amber-200/80'>
        · {ROLE_WORDS[role]}
      </span>
    </div>
  );
}

/**
 * In a region node's config panel: each attempt's output for this node,
 * read from the loop's result, with the last one open.
 */
export function RegionNodeRunCard({ nodeId }: { nodeId: string }) {
  const membership = useRegionMembership(nodeId);
  const [open, setOpen] = useState<number | null>(null);
  if (!membership) return null;
  const { run, role, carrierName } = membership;
  return (
    <section
      className='mx-3 my-2 space-y-2 rounded-md border border-amber-200 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/15 p-2 text-xs'
      data-testid='repeat-region-card'
    >
      <div className='flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-100'>
        <RotateCcw className='size-3.5' />
        {role === 'entry'
          ? 'This is the step that repeats'
          : role === 'evaluator'
            ? 'This node judges each attempt'
            : 'This node runs inside the loop'}
      </div>
      <p className='text-[11px] text-amber-900/80 dark:text-amber-100/80'>
        Part of the loop <strong>{carrierName}</strong>. It runs once per
        attempt;
        {role === 'entry'
          ? ' the first attempt uses the inputs set here, later attempts receive the judge\u2019s feedback.'
          : ' its result for each attempt is kept on the loop node.'}
      </p>
      {run ? (
        <div className='space-y-1'>
          <div className='text-[11px] font-medium text-amber-900 dark:text-amber-100'>
            Ran {run.count} {run.count === 1 ? 'time' : 'times'}
            {run.skipped ? `, skipped ${run.skipped}` : ''} on the last run
          </div>
          <ol className='space-y-1'>
            {run.iterations.map((entry) => {
              const isOpen =
                open === entry.iteration ||
                (open === null &&
                  entry.iteration ===
                    run.iterations[run.iterations.length - 1].iteration);
              return (
                <li
                  key={entry.iteration}
                  className='rounded border bg-background'
                  data-testid={`region-iteration-${entry.iteration}`}
                >
                  <button
                    type='button'
                    className='flex w-full items-center gap-1.5 px-2 py-1 text-left text-[11px]'
                    onClick={() => setOpen(isOpen ? -1 : entry.iteration)}
                  >
                    {isOpen ? (
                      <ChevronDown className='size-3' />
                    ) : (
                      <ChevronRight className='size-3' />
                    )}
                    Attempt {entry.iteration}
                    {(entry.skipSentence || !isOpen) && (
                      <span
                        className='ml-1 truncate text-muted-foreground'
                        data-testid={
                          entry.skipSentence
                            ? `region-iteration-skip-${entry.iteration}`
                            : undefined
                        }
                      >
                        {entry.skipSentence ??
                          JSON.stringify(entry.output).slice(0, 60)}
                      </span>
                    )}
                  </button>
                  {isOpen && !entry.skipSentence && (
                    <pre
                      className={cn(
                        'max-h-40 overflow-auto border-t p-1.5 text-[10px] leading-4',
                      )}
                    >
                      {JSON.stringify(entry.output, null, 1)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      ) : (
        <div className='text-[11px] text-amber-900/80 dark:text-amber-100/80'>
          Not run yet.
        </div>
      )}
    </section>
  );
}
